import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ICompetition } from 'src/interfaces/Competition';
import { IEdition } from 'src/interfaces/Edition';
import { TechnicalType } from 'src/interfaces/TechnicalType';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

interface UseEditionCompetitionQueryParamsOptions {
  defaultYear?: number;
  defaultCompetitionId?: string;
  /** When true, missing `year` query param means "All editions" (dashboard). */
  allowAllYears?: boolean;
}

function isCompetitionInEdition(
  edition: IEdition | undefined,
  competitionName: string,
  competitionsById: Record<string, ICompetition> | undefined,
): boolean {
  if (!edition || !competitionsById) return false;
  return (edition.competitions ?? []).some(ec => {
    const comp = competitionsById[ec.competitionId];
    return comp?.name === competitionName;
  });
}

function isDistanceInEditionCompetition(
  edition: IEdition | undefined,
  competitionId: string | undefined,
  distanceId: string,
): boolean {
  if (!edition || !competitionId) return false;
  const ec = (edition.competitions ?? []).find(c => c.competitionId === competitionId);
  return Boolean(ec?.distances?.includes(distanceId));
}

export function useEditionCompetitionQueryParams(
  options: UseEditionCompetitionQueryParamsOptions = {},
) {
  const { defaultYear, defaultCompetitionId, allowAllYears = false } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const hasInitializedYear = useRef(false);

  const {
    editions,
    competitionsById,
    competitionsByName,
    distancesById,
    distancesByName,
    activeEditionId,
  } = useSystemOptionsStore(s => s);

  const resultsEditions = useMemo(
    () => [...(editions ?? [])].sort((a, b) => b.year - a.year),
    [editions],
  );

  const defaultYearValue = useMemo(() => {
    if (defaultYear && resultsEditions.some(e => e.year === defaultYear)) {
      return defaultYear;
    }
    if (activeEditionId) {
      const active = resultsEditions.find(e => e.id === activeEditionId);
      if (active) return active.year;
    }
    return resultsEditions[0]?.year;
  }, [defaultYear, resultsEditions, activeEditionId]);

  const yearFromUrl = searchParams.get('year');
  const parsedYear = yearFromUrl ? Number(yearFromUrl) : undefined;
  const selectedYear = useMemo((): number | null => {
    if (allowAllYears && !yearFromUrl) return null;
    if (parsedYear && resultsEditions.some(e => e.year === parsedYear)) {
      return parsedYear;
    }
    if (allowAllYears) return null;
    return defaultYearValue ?? null;
  }, [allowAllYears, yearFromUrl, parsedYear, resultsEditions, defaultYearValue]);

  const selectedEdition = useMemo(() => {
    if (selectedYear == null) return undefined;
    return resultsEditions.find(e => e.year === selectedYear);
  }, [selectedYear, resultsEditions]);

  const competitionsForEdition = useMemo(() => {
    if (!selectedEdition || !competitionsById) return [] as ICompetition[];
    return (selectedEdition.competitions ?? [])
      .map(ec => competitionsById[ec.competitionId])
      .filter((c): c is ICompetition => Boolean(c));
  }, [selectedEdition, competitionsById]);

  const selectedCompetitionName = useMemo((): string | undefined => {
    const fromUrl = searchParams.get('competition');
    if (!fromUrl || !competitionsByName?.[fromUrl]) return undefined;
    if (selectedEdition) {
      return isCompetitionInEdition(selectedEdition, fromUrl, competitionsById)
        ? fromUrl
        : undefined;
    }
    return fromUrl;
  }, [searchParams, competitionsByName, selectedEdition, competitionsById]);

  const competition = selectedCompetitionName
    ? competitionsByName?.[selectedCompetitionName]
    : undefined;

  const editionCompetition = useMemo(() => {
    if (!selectedEdition || !competition) return undefined;
    return (selectedEdition.competitions ?? []).find(ec => ec.competitionId === competition.id);
  }, [selectedEdition, competition]);

  const distances = editionCompetition?.distances ?? [];

  const distancesForCompetition = useMemo((): TechnicalType[] => {
    if (!distancesById) return [];
    return distances
      .map(id => distancesById[id])
      .filter((d): d is TechnicalType => Boolean(d));
  }, [distances, distancesById]);

  const selectedDistanceName = useMemo((): string | undefined => {
    const fromUrl = searchParams.get('distance');
    if (!fromUrl || !distancesByName?.[fromUrl]) return undefined;
    const distance = distancesByName[fromUrl];
    if (!editionCompetition) return undefined;
    return editionCompetition.distances.includes(distance.id) ? fromUrl : undefined;
  }, [searchParams, distancesByName, editionCompetition]);

  const selectedDistanceId = selectedDistanceName
    ? distancesByName?.[selectedDistanceName]?.id
    : undefined;

  useEffect(() => {
    if (hasInitializedYear.current || allowAllYears) return;
    if (yearFromUrl) {
      hasInitializedYear.current = true;
      return;
    }
    if (defaultYearValue == null) return;
    hasInitializedYear.current = true;
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('year', String(defaultYearValue));
        return next;
      },
      { replace: true },
    );
  }, [allowAllYears, yearFromUrl, defaultYearValue, setSearchParams]);

  const setYear = useCallback(
    (newYear: number | null) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (newYear == null) {
            next.delete('year');
          } else {
            next.set('year', String(newYear));
          }

          const edition =
            newYear != null ? resultsEditions.find(e => e.year === newYear) : undefined;

          const compName = next.get('competition');
          if (compName) {
            if (
              !edition ||
              !isCompetitionInEdition(edition, compName, competitionsById)
            ) {
              next.delete('competition');
              next.delete('distance');
            }
          }

          const distName = next.get('distance');
          if (distName && next.get('competition')) {
            const remainingCompName = next.get('competition');
            const remainingComp = remainingCompName
              ? competitionsByName?.[remainingCompName]
              : undefined;
            const distance = distancesByName?.[distName];
            if (
              !distance ||
              !isDistanceInEditionCompetition(edition, remainingComp?.id, distance.id)
            ) {
              next.delete('distance');
            }
          } else if (distName && !next.get('competition')) {
            next.delete('distance');
          }

          return next;
        },
        { replace: true },
      );
    },
    [
      setSearchParams,
      resultsEditions,
      competitionsById,
      competitionsByName,
      distancesByName,
    ],
  );

  const setCompetition = useCallback(
    (compName: string | undefined) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (!compName) {
            next.delete('competition');
            next.delete('distance');
          } else {
            next.set('competition', compName);
            if (selectedYear != null) {
              next.set('year', String(selectedYear));
            } else if (defaultYearValue != null) {
              next.set('year', String(defaultYearValue));
            }

            const year =
              selectedYear ?? defaultYearValue ?? undefined;
            const edition =
              year != null ? resultsEditions.find(e => e.year === year) : selectedEdition;
            const newComp = competitionsByName?.[compName];
            const distName = next.get('distance');
            if (distName) {
              const distance = distancesByName?.[distName];
              if (
                !distance ||
                !isDistanceInEditionCompetition(edition, newComp?.id, distance.id)
              ) {
                next.delete('distance');
              }
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [
      setSearchParams,
      selectedYear,
      defaultYearValue,
      resultsEditions,
      selectedEdition,
      competitionsByName,
      distancesByName,
    ],
  );

  const setDistance = useCallback(
    (distanceName: string | undefined) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (!distanceName) {
            next.delete('distance');
          } else {
            next.set('distance', distanceName);
            if (selectedYear != null) {
              next.set('year', String(selectedYear));
            } else if (defaultYearValue != null) {
              next.set('year', String(defaultYearValue));
            }
            if (selectedCompetitionName) {
              next.set('competition', selectedCompetitionName);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, selectedYear, defaultYearValue, selectedCompetitionName],
  );

  const clearCompetition = useCallback(() => {
    setCompetition(undefined);
  }, [setCompetition]);

  return {
    selectedYear,
    selectedCompetitionName,
    selectedDistanceName,
    selectedDistanceId,
    selectedEdition,
    competition,
    competitionsForEdition,
    distancesForCompetition,
    resultsEditions,
    distances,
    setYear,
    setCompetition,
    setDistance,
    clearCompetition,
    defaultCompetitionId,
  };
}
